import { PrimaryButton } from '@systemeio/ui'
import { AddIcon } from '@systemeio/ui/icons'
import { removeProduct } from 'modules/products/api/product-api'
import { ProductsLoco } from 'modules/products/constants/products-loco'
import { useProducts } from 'modules/products/hooks/use-products'
import { ProductInterface } from 'modules/products/types/product-interface'
import { NextPage } from 'next'
import LinkWithoutPrefetch from 'shared/components/link-without-prefetch'
import ListingLayout from 'shared/components/listing-layout'
import { Table } from 'shared/components/table/table'
import { ColumnDefinitionInterface } from 'shared/components/table/types/column-interface'
import { RequestModuleEnum } from 'shared/enums/request-module-enum'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { useRevalidateModules } from 'shared/hooks/use-revalidate-modules'
import useUser from 'shared/hooks/use-user'
import { usePagination } from 'shared/route-query/hooks/use-pagination'
import { dateTimeFormat } from 'shared/utils/date-time-format'
import { getBaseServerSideProps } from 'shared/utils/get-base-server-side-props'

const Product: NextPage = () => {
  const { user } = useUser()
  const { setPagination, pagination, setLimit, limit } = usePagination()

  const { products, counter } = useProducts({ limit, pagination })
  const { t } = useLocoTranslation()

  const { revalidateModules } = useRevalidateModules()

  const columns: ColumnDefinitionInterface<ProductInterface>[] = [
    {
      key: 'name',
      header: t('dashboard.products.list.name_header'),
      widthPercent: 50,
      onRender: product => (
        <LinkWithoutPrefetch
          className="primary-link w-full] block overflow-hidden text-ellipsis whitespace-nowrap"
          href={`/products/${product.id}`}
        >
          {product.name}
        </LinkWithoutPrefetch>
      ),
    },
    {
      key: 'name',
      header: t('dashboard.products.list.tax_header'),
      widthPercent: 20,
      onRender: product => (
        <div className="text-text-secondary w-full] block overflow-hidden text-ellipsis whitespace-nowrap">
          {product.tax} %
        </div>
      ),
      onRenderSmall: product => <span>{product.tax}</span>,
    },
    {
      key: 'created_at',
      header: t('global.created_on'),
      widthPercent: 30,
      onRender: product => (
        <span>
          {product.createdAt &&
            dateTimeFormat(product.createdAt, {
              timeFormat: user?.timeFormat,
              dateFormat: user?.dateFormat,
              timeZone: user?.timezone,
            })}
        </span>
      ),
      onRenderSmall: product => (
        <span>{`${t('global.created_on')}: ${
          product.createdAt &&
          dateTimeFormat(product.createdAt, {
            timeFormat: user?.timeFormat,
            dateFormat: user?.dateFormat,
            timeZone: user?.timezone,
          })
        }`}</span>
      ),
    },
  ]

  const onRemoveProduct = async (product: ProductInterface) => {
    await removeProduct(product.id)
    await revalidateModules([RequestModuleEnum.PhysicalProducts], true)
  }

  return (
    <ListingLayout
      counter={counter}
      data={products}
      title={t('dashboard.products.list.header')}
      type={'compact'}
      customHeaderElement={
        <PrimaryButton
          as={LinkWithoutPrefetch}
          className="bg-brand-primary"
          href={'/products/create'}
        >
          <AddIcon />
          <span> {t('dashboard.products.create')}</span>
        </PrimaryButton>
      }
    >
      <Table
        caption={t('dashboard.product.title')}
        data={products}
        columns={columns}
        limiter={{ limit, setLimit }}
        paginator={{ pagination, setPagination }}
        actions={{
          remove: {
            onChange: product => onRemoveProduct(product),
          },
          edit: {
            setHref: product => `/products/${product.id}`,
            isNext: true,
          },
        }}
      />
    </ListingLayout>
  )
}

export const getServerSideProps = getBaseServerSideProps(
  ProductsLoco,
  'header.top_menu.physical_product',
)

export default Product
